
import type { } from 'gsap';
import { isMobileViewport } from '../utilities/ViewportHelpers';

export abstract class ScrollAnimations {

    static _settings = {
        attributes: {
            scrollTrigger_enableMarkers: 'gsap-scrollTrigger-marker'
        }
    };

    /**
     * Makes Animate.css animations available on scroll into view with HTML Data Attributes
     * @example
     * <div animate-utility="animate__fadeIn animate__fast" scrolltrigger-trigger-start="top-=200 center">...</div>
     */
    public static InitScrollTriggerForAnimateCSS(): void {

        document.addEventListener("DOMContentLoaded", () => {
            // get animate.css scene components from animate-utility
            const animateUtilityEls = [...document.querySelectorAll<HTMLElement>("[animate-utility]")];

            animateUtilityEls.forEach((el, index) => {
                el.classList.add("opacity-0", "animate__animated");

                const scrollTriggerStart = el.getAttribute("scrolltrigger-trigger-start");

                ScrollTrigger.create({
                    trigger: el.closest('.component'),
                    id: `${index + 1} css scroll`,
                    start: scrollTriggerStart ? scrollTriggerStart : 'top center',
                    onEnter: () => {
                        el.classList.remove("opacity-0");
                        el.classList.add(...el.getAttribute("animate-utility").split(" "));
                    },
                    markers: false,
                    once: true
                });
            });
        }, false);
    }

    /**
     * Makes GSAP tween elements within a component animate when the component is dressed with [gsap-timeline] and [gsap-scrollTrigger] attributes
     */
    public static InitTweenAnimations(): void {
        document.addEventListener('DOMContentLoaded', () => {

            setTimeout(() => {
                const timelines = [...document.querySelectorAll<HTMLElement>("[gsap-timeline]")];
                const isMobile = isMobileViewport();

                timelines.forEach((el, index) => {

                    // set optional scroll-trigger options from component data attributes
                    const customScrollOptions: ScrollTrigger.StaticVars = {};
                    if (el.getAttribute("gsap-scrollTrigger-toggle-actions"))
                        customScrollOptions.toggleActions = el.getAttribute("gsap-scrollTrigger-toggle-actions");
                    if (el.getAttribute("gsap-scrollTrigger-once") === "true")
                        customScrollOptions.once = el.getAttribute("gsap-scrollTrigger-once") === 'true';
                    if (el.getAttribute("gsap-scrollTrigger-start"))
                        customScrollOptions.start = el.getAttribute("gsap-scrollTrigger-start");
                    if (el.getAttribute("gsap-scrollTrigger-end"))
                        customScrollOptions.end = el.getAttribute("gsap-scrollTrigger-end");
                    if (el.getAttribute("gsap-scrollTrigger-end-mobile") && isMobile)
                        customScrollOptions.end = el.getAttribute("gsap-scrollTrigger-end-mobile");

                    // disable scrub backwards (no reverse animation on scroll up)
                    const disableScrubBackwards = el.getAttribute("gsap-scrollTrigger-disable-scrub-backwards") !== null;

                    const customDefaultOptions: gsap.TweenVars = {};
                    if (el.getAttribute("gsap-defaults-ease"))
                        customDefaultOptions.ease = el.getAttribute("gsap-defaults-ease");
                    if (el.getAttribute("gsap-defaults-ease-mobile") && isMobile)
                        customDefaultOptions.ease = el.getAttribute("gsap-defaults-ease-mobile");

                    // create the component GSAP timeline
                    const tl = gsap.timeline({
                        paused: true,
                        defaults: {
                            ...customDefaultOptions,
                        }
                    });

                    // create the inner tweens per element
                    const tweens = [...el.querySelectorAll<HTMLElement>(":scope [gsap-tween-vars]")];
                    tweens.forEach((tweenEl) => {

                        try {
                            const varsObjArr = Array.isArray(JSON.parse(tweenEl.getAttribute("gsap-tween-vars"))) ?
                                JSON.parse(tweenEl.getAttribute("gsap-tween-vars")) : [JSON.parse(tweenEl.getAttribute("gsap-tween-vars"))];

                            varsObjArr.forEach(tweenObj => {
                                let pos = 0;
                                if ('position' in tweenObj) {
                                    pos = tweenObj.position;
                                    delete tweenObj.position;
                                }
                                tl.from(
                                    tweenEl,
                                    tweenObj,
                                    pos);
                            });
                        } catch { }
                    });

                    // check if any element tweens are controlled at the component level with [gsap-tween-elements] instead of inner tweens per element
                    const componentTweens = el.getAttribute("gsap-tween-elements");
                    if (componentTweens !== null && componentTweens.length) {
                        try {
                            const componentTweensArr = Array.isArray(JSON.parse(componentTweens)) ?
                                JSON.parse(componentTweens) : [JSON.parse(componentTweens)];

                            componentTweensArr.forEach((tween) => {
                                if ('selector' in tween && 'vars' in tween && el.querySelector<HTMLElement>(tween.selector) !== null) {
                                    const tweenEl = el.querySelector<HTMLElement>(tween.selector);

                                    tl.from(
                                        tweenEl,
                                        tween.vars,
                                        0);
                                }
                            });

                        } catch { }

                    }

                    // create the ScrollTrigger instance for the component
                    ScrollTrigger.create({
                        ...customScrollOptions,
                        trigger: el,
                        markers: el.getAttribute(this._settings.attributes.scrollTrigger_enableMarkers) !== null,
                        id: `${index + 1} tween scroll`,
                        onUpdate: ({ progress }) => {
                            if (progress > tl.progress()) {
                                gsap.to(tl, {
                                    ease: "power3",
                                    progress
                                })
                            } else if (!disableScrubBackwards) {
                                // if backwards scrubbing isn't disabled, move backwards
                                gsap.to(tl, {
                                    ease: "power3",
                                    progress
                                })
                            }
                        }
                    });
                });
            }, 250);
        }, false);
    }
}